import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError, getMe, getTicket, getTicketDisplay, type TicketState } from '../lib/api-client';
import { useI18n } from '../lib/i18n-context';
import { TicketQr } from '../lib/qr';
import { rememberTicket } from '../lib/ticket-history';

/** state badge 顏色（valid/used/voided/expired）。 */
function stateBadgeStyle(state: TicketState): React.CSSProperties {
  const palette: Record<TicketState, { bg: string; fg: string }> = {
    valid: { bg: '#d4edda', fg: '#155724' },
    used: { bg: '#d1ecf1', fg: '#0c5460' },
    void_pending_refund: { bg: '#fff3cd', fg: '#856404' },
    voided: { bg: '#f8d7da', fg: '#721c24' },
    expired: { bg: '#e2e3e5', fg: '#383d41' },
  };
  const p = palette[state];
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    background: p.bg,
    color: p.fg,
  };
}

/**
 * 票券 QR 顯示頁。
 *
 * - `GET /tickets/:id` 取 ticket 詳情
 * - QR 內容 = `qr_payload`（compact JWS）；督導 / Kiosk 掃描可走 POST /tickets/verify
 * - 顯示 ticket_code（人類可讀備援）+ 狀態 badge + 風險級別
 */
export function TicketQRPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useI18n();
  const query = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => {
      if (id === undefined) throw new Error('missing ticket id');
      return getTicket(id);
    },
    enabled: id !== undefined,
    // 票券資訊（含 state）可能因 check-in 而變；不長 stale
    staleTime: 5_000,
  });

  // 票券 display DTO（多語文案 + can_board）；後端依 Accept-Language 在地化 message。
  // queryKey 含 locale → 切語言時 refetch，display.message 隨之更新。
  const displayQuery = useQuery({
    queryKey: ['ticket-display', id, locale],
    queryFn: () => {
      if (id === undefined) throw new Error('missing ticket id');
      return getTicketDisplay(id);
    },
    enabled: id !== undefined,
    staleTime: 5_000,
  });

  // 拉到票券後寫 history（補 ticket_code；OrderConfirm 寫入時只有 ticket_id）
  const meQuery = useQuery({ queryKey: ['auth', 'me'], queryFn: getMe, retry: false });
  useEffect(() => {
    if (!query.isSuccess || id === undefined) return;
    rememberTicket(meQuery.data?.user_id, {
      ticketId: id,
      ticketCode: query.data.ticket_code,
    });
  }, [query.isSuccess, query.data?.ticket_code, id, meQuery.data?.user_id]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <h1>{t('ticket.title')}</h1>
        <Link to="/" className="topbar-back">
          {t('common.backHome')}
        </Link>
      </header>

      {query.isPending && <p>{t('common.loading')}</p>}
      {query.isError && (
        <p className="error-text">
          {t('ticket.loadFailed')}
          {query.error instanceof ApiError
            ? (query.error.problem?.detail ?? query.error.message)
            : t('common.unknownError')}
        </p>
      )}
      {query.isSuccess && (
        <article className="card" style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 12px' }}>
            <div
              style={{
                background: 'var(--boat-bg)',
                border: '1px solid var(--boat-border-light)',
                borderRadius: 12,
                padding: 16,
                lineHeight: 0,
              }}
            >
              <TicketQr value={query.data.qr_payload} size={240} level="M" />
            </div>
          </div>
          <p style={{ fontFamily: 'monospace', fontSize: 16, letterSpacing: 1 }}>
            {query.data.ticket_code}
          </p>
          <p>
            <span style={stateBadgeStyle(query.data.ticket_state)}>{query.data.ticket_state}</span>
            <span
              style={{
                marginLeft: 8,
                fontSize: 12,
                color: query.data.refund_risk_class === 'REFUND_LOCKED' ? '#D93025' : '#777',
              }}
            >
              {query.data.refund_risk_class === 'REFUND_LOCKED'
                ? t('ticket.refundLocked')
                : t('ticket.refundEligible')}
            </span>
          </p>
          <p style={{ fontSize: 12, color: '#777' }}>{t('ticket.qrNote')}</p>
          {displayQuery.isSuccess && (
            <p style={{ fontSize: 13, color: '#555' }}>
              {displayQuery.data.display.message}
              <span
                style={{
                  marginLeft: 8,
                  fontWeight: 500,
                  color: displayQuery.data.display.can_board ? '#1E8E3E' : '#D93025',
                }}
              >
                {displayQuery.data.display.can_board
                  ? t('ticket.canBoard')
                  : t('ticket.cannotBoard')}
              </span>
            </p>
          )}
          {query.data.ticket_state === 'valid' &&
            query.data.refund_risk_class === 'REFUND_ELIGIBLE' && (
              <Link
                to={`/tickets/${id}/refund`}
                className="btn-secondary btn-block"
                style={{ textDecoration: 'none', marginTop: 12 }}
              >
                {t('ticket.applyRefund')}
              </Link>
            )}
        </article>
      )}
    </div>
  );
}
